<script>
  import FormSection from "@components/shared/form-section.svelte"
  import FormLabel from "@components/shared/form-label.svelte"
  import Autocomplete from "@components/shared/autocomplete.svelte"
  import SegmentedControl from "@components/shared/segmented-control.svelte"
  import isEqualObject from "is-equal-shallow"
  import { organisationTypes, capitalize, getCompanyType } from "@lib/utils"
  
  let pappersKey = $derived(document.querySelector("body").dataset.pkv)
  
  const defaultData = {
    name: "",
    type: "",
    siren: "",
    siret: "",
    tax_number: "",
    tax_scheme: "standard",
    ede: "",
    breeder: false,
    contracted: false
  }
  
  let { data = $bindable(defaultData), onSelect } = $props()

  let container = $state(null)
  let filled = $derived(!isEqualObject(data, defaultData))

  let autocomplete = $state(null)
  let lookupResults = $state([])
  let autocompleteFocused = $state(false)
  let organisations = $derived(lookupResults?.resultats_nom_entreprise || [])
  let focused = $state(false)

  async function selectOrganisation(organisation) {
    lookupResults = []
    autocomplete?.clearQuery()

    data = {
      name: capitalize(organisation.nom_entreprise),
      siren: organisation.siren,
      type: getCompanyType(organisation.forme_juridique),
    }

    const siege = organisation.siege

    if (siege) {
      if (siege.siret) data.siret = siege.siret
      if (siege.adresse_ligne_1) data.street_1 = siege.adresse_ligne_1
      if (siege.adresse_ligne_2) data.street_2 = siege.adresse_ligne_2
      if (siege.code_postal) data.postcode = siege.code_postal
      if (siege.ville) data.town = siege.ville
      if (siege.code_pays) data.country = siege.code_pays
    }

    if (data.siret && pappersKey) {
      const url = `https://api.pappers.fr/v2/entreprise?api_token=${pappersKey}&siret=${data.siret}`
      const response = await fetch(url, { headers: { accept: "application/json" } })
      const companyData = await response.json()
      if (companyData?.numero_tva_intracommunautaire) {
        data.tax_number = companyData.numero_tva_intracommunautaire
      }
    }

    onSelect?.(data)
    
  }

  export function focusLookup() {
    autocomplete?.focus()
  }

  export function scrollIntoView() {
    container?.scrollIntoView({ behavior: "smooth" })
  }

</script>

<section bind:this={container} class="contents">

  <FormSection label="Organisation">

    <div class="form-item col-span-12" class:opacity-50={filled && !focused}>
      
      <p class="label">Rechercher une organisation</p>
      
      <Autocomplete 
        bind:this={autocomplete}
        bind:items={lookupResults}
        url="https://suggestions.pappers.fr/v2"
        showList={false}
        onClear={() => lookupResults = []}
        onFocus={() => focused = true}
        onBlur={() => focused = false}
      />
      
      {#if organisations.length > 0}
        <div class="grid grid-cols-3 gap-4 mt-6">
          {#each organisations as organisation (organisation.siren)}
            <button 
              class="bg-yellow-100/80 p-4 text-smaller text-left text-gray-500 border border-yellow-200 rounded-sm shadow max-h-[20rem] overflow-y-auto flex flex-col items-start justify-start" 
              onclick={() => selectOrganisation(organisation)}>
              <h3 class="font-semibold">{organisation.nom_entreprise}</h3>
              <p>{organisation.domaine_activite}</p>
              <p>Siren: {organisation.siren_formate}</p>
            </button>
          {/each}
        </div>
      {/if}

    </div>

    <div class="form-item col-span-4">
      <label for="organisation_name">Type de l'organisation</label>
      <select name="organisation_type" id="organisation_type" class="select" bind:value={data.type}>
        {#each organisationTypes as type}
          <option value={type}>{type}</option>
        {/each}
      </select>
    </div>

    <div class="form-item col-span-8">
      <label for="organisation_name">Nom de l'organisation</label>
      <input type="text" name="organisation_name" id="organisation_name" bind:value={data.name} required>
    </div>

    <div class="form-item col-span-6">
      <label for="organisation_siren">SIREN</label>
      <input type="text" name="organisation_siren" id="organisation_siren" bind:value={data.siren}>
    </div>

    <div class="form-item col-span-6">
      <label for="organisation_siret">SIRET</label>
      <input type="text" name="organisation_siret" id="organisation_siret" bind:value={data.siret}>
    </div>

    <div class="form-item col-span-6">
      <label for="organisation_tva">Numéro de TVA</label>
      <input type="text" name="organisation_tva" id="organisation_tva" bind:value={data.tax_number}>
    </div>

    <div class="form-item col-span-6">
      <label for="organisation_ede">Numéro EDE</label>
      <input type="text" name="organisation_ede" id="organisation_ede" bind:value={data.ede}>
    </div>

  <FormLabel label="Elévage" />

    <div class="col-span-12 flex items-center flex-wrap gap-6">

      <!-- breeder and contracted checkboxed -->
      <label for="organisation_breeder" class="flex items-center gap-3">
        <input class="input !p-0" type="checkbox" name="organisation_breeder" id="organisation_breeder" bind:checked={data.breeder}>
        <span>Elévage</span>
      </label>

      <label for="organisation_contracted" class="flex items-center gap-3">
        <input class="input !p-0" type="checkbox" name="organisation_contracted" id="organisation_contracted" bind:checked={data.contracted}>
        <span>Contrat</span>
      </label>

    </div>

    <FormLabel label="Type de taxation" />

    <div class="form-item col-span-12">
      <label for="organisation_tax_scheme">Type de taxation</label>
      <SegmentedControl 
        bind:value={data.tax_scheme}
        options={[
          { label: "Français", value: "standard" },
          { label: "Exonéré", value: "exempt" },
          { label: "Export", value: "export" }
        ]}
      />
    </div>

</FormSection>

</section>
